/**
 * StaticPage — generic CMS-driven page used by all admin-editable static routes.
 *
 * Reads admin overrides via useSiteContent(pageId). When body_html is empty,
 * falls back to a hardcoded default panel.
 *
 * Supports optional inline forms:
 *   contactForm = 'careers' → render CareersForm
 *   contactForm = 'support' → render SupportForm
 */
import React from 'react';
import { Sparkles } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';
import { CareersForm } from './forms/CareersForm';
import { SupportForm } from './forms/SupportForm';
import { dangerousSafeHtml } from '../utils/sanitizeHtml';

export const StaticPage = ({
  pageId,
  defaultTitle,
  defaultSubtitle,
  defaultBodyHtml,
  tag,
  contactForm = null,   // 'careers' | 'support' | null
  extraContent = null,  // optional JSX appended below body
}) => {
  const { sections: c } = useSiteContent(pageId);
  const title = c.hero_title || defaultTitle;
  const subtitle = c.hero_subtitle_html || defaultSubtitle;
  const bodyHtml = c.body_html || defaultBodyHtml;

  return (
    <div className="min-h-screen bg-white" data-testid={`static-page-${pageId}`}>
      {/* HERO */}
      <section className="relative pt-12 pb-12 bg-gradient-to-b from-[#FFF5F8] via-white to-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#FF4D8A] rounded-full opacity-10 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {tag && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFE5EE] text-[#FF0059] text-xs font-bold mb-3" data-testid={`${pageId}-tag`}>
              <Sparkles className="h-3.5 w-3.5" /> {tag}
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-black mb-3 leading-[1.05]" data-testid={`${pageId}-title`}>
            {title}
          </h1>
          {subtitle && (
            <div
              className="text-base sm:text-lg text-gray-600 max-w-2xl prose prose-sm prose-p:my-1"
              dangerouslySetInnerHTML={dangerousSafeHtml(subtitle)}
              data-testid={`${pageId}-subtitle`}
            />
          )}
        </div>
      </section>

      {/* BODY */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {bodyHtml ? (
          <div
            className="prose prose-lg max-w-none prose-headings:font-black prose-headings:text-black prose-a:text-[#FF0059] prose-strong:text-black"
            dangerouslySetInnerHTML={dangerousSafeHtml(bodyHtml)}
            data-testid={`${pageId}-body`}
          />
        ) : null}
        {extraContent}
        {contactForm === 'careers' && (
          <div className="mt-10" data-testid={`${pageId}-careers-form-wrap`}>
            <CareersForm pageId={pageId} />
          </div>
        )}
        {contactForm === 'support' && (
          <div className="mt-10" data-testid={`${pageId}-support-form-wrap`}>
            <SupportForm pageId={pageId} />
          </div>
        )}
      </section>
    </div>
  );
};
